import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../api';
import { encodeWav } from '../Assistant/useVoiceConversation';

// One-shot dictation for a search field: start() records the mic, stops on its own after a
// pause once something was said (or on stop()), sends the audio as WAV to our voice service
// (faster-whisper, /api/v1/assistant/voice/transcribe — same as the assistant) and calls
// onResult(text). If the voice service is unreachable, falls back to the browser's own speech
// recognition when there is one (Chrome / Edge).
//
// state: 'idle' | 'listening' | 'transcribing'

const TRANSCRIBE_URL = `${API_BASE_URL}/api/v1/assistant/voice/transcribe`;

const AudioContextClass = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;
const SpeechRecognition = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
const isBrave = typeof navigator !== 'undefined' && !!navigator.brave;
const browserRecognitionSupported = !!SpeechRecognition && !isBrave;

export const isDictationSupported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia && !!AudioContextClass;

const SILENCE_TO_END_MS = 900;   // pause that ends the dictation, once speech started
const NO_SPEECH_TIMEOUT_MS = 6000; // nothing said at all → give up
const MAX_MS = 10000;            // a search is short
const MIN_THRESHOLD = 0.012;     // RMS floor for "speech"

const isUnavailable = (err) => !err.response || [502, 503, 504].includes(err.response.status);

// "Doliprane." → "Doliprane" (whisper adds sentence punctuation a search doesn't want)
const cleanTranscript = (text) => (text || '').trim().replace(/[.!?…]+$/, '').trim();

export default function useVoiceDictation({ onResult, onError }) {
    const [state, setState] = useState('idle');
    const [level, setLevel] = useState(0); // 0..1 mic level, for the button's pulse
    const audioRef = useRef(null);
    const captureRef = useRef(null);
    const recognitionRef = useRef(null);
    const onResultRef = useRef(onResult);
    const onErrorRef = useRef(onError);
    onResultRef.current = onResult;
    onErrorRef.current = onError;

    const stopAudio = useCallback(() => {
        const a = audioRef.current;
        audioRef.current = null;
        if (!a) return;
        try { a.processor.disconnect(); a.source.disconnect(); } catch { /* already disconnected */ }
        a.stream.getTracks().forEach(t => t.stop());
        a.ctx.close().catch(() => {});
    }, []);

    // browser fallback (no voice service): one recognition, final result only
    const listenBrowser = useCallback(() => {
        const recognition = new SpeechRecognition();
        recognition.lang = 'fr-FR';
        recognition.interimResults = false;
        recognition.continuous = false;
        recognitionRef.current = recognition;
        setState('listening');
        recognition.onresult = (e) => {
            const text = cleanTranscript(e.results?.[0]?.[0]?.transcript);
            if (text) onResultRef.current?.(text);
        };
        recognition.onerror = () => onErrorRef.current?.("Je n'ai pas pu comprendre. Réessayez.");
        recognition.onend = () => { recognitionRef.current = null; setState('idle'); };
        recognition.start();
    }, []);

    const finish = useCallback(async () => {
        const capture = captureRef.current;
        captureRef.current = null;
        const sampleRate = audioRef.current?.ctx.sampleRate;
        stopAudio();
        setLevel(0);
        if (!capture || !capture.started || !sampleRate) { setState('idle'); return; }

        setState('transcribing');
        try {
            const res = await axios.post(TRANSCRIBE_URL, encodeWav(capture.chunks, sampleRate), {
                withCredentials: true,
                headers: { 'Content-Type': 'audio/wav' },
            });
            const text = cleanTranscript(res.data?.text);
            if (text) onResultRef.current?.(text);
            else onErrorRef.current?.("Je n'ai rien entendu. Réessayez.");
            setState('idle');
        } catch (err) {
            setState('idle');
            if (isUnavailable(err) && browserRecognitionSupported) {
                listenBrowser(); // voice service unreachable: dictate again with the browser engine
            } else if (err.response?.status === 403) {
                onErrorRef.current?.("La recherche vocale nécessite Saydalani AI (Paramètres → Saydalani AI).");
            } else {
                onErrorRef.current?.(isUnavailable(err)
                    ? 'Le service vocal est momentanément indisponible.'
                    : "Je n'ai pas pu transcrire l'audio.");
            }
        }
    }, [stopAudio, listenBrowser]);

    const start = useCallback(async () => {
        if (state !== 'idle') return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            let ctx;
            try { ctx = new AudioContextClass({ sampleRate: 16000 }); } catch { ctx = new AudioContextClass(); }
            const source = ctx.createMediaStreamSource(stream);
            const processor = ctx.createScriptProcessor(4096, 1, 1);
            const msPerBuffer = (4096 / ctx.sampleRate) * 1000;
            let noiseFloor = 0.005;
            captureRef.current = { chunks: [], started: false, silenceMs: 0, totalMs: 0 };

            processor.onaudioprocess = (e) => {
                const capture = captureRef.current;
                if (!capture) return;
                const samples = new Float32Array(e.inputBuffer.getChannelData(0));
                let sum = 0;
                for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
                const rms = Math.sqrt(sum / samples.length);
                const threshold = Math.max(MIN_THRESHOLD, noiseFloor * 3);
                setLevel(Math.min(1, rms / (threshold * 4)));
                capture.totalMs += msPerBuffer;

                if (!capture.started) {
                    capture.chunks = capture.chunks.slice(-2); // short pre-roll before the first word
                    capture.chunks.push(samples);
                    if (rms > threshold) capture.started = true;
                    else noiseFloor = noiseFloor * 0.95 + rms * 0.05;
                    if (!capture.started && capture.totalMs >= NO_SPEECH_TIMEOUT_MS) finish();
                    return;
                }
                capture.chunks.push(samples);
                capture.silenceMs = rms > threshold ? 0 : capture.silenceMs + msPerBuffer;
                if (capture.silenceMs >= SILENCE_TO_END_MS || capture.totalMs >= MAX_MS) finish();
            };
            source.connect(processor);
            processor.connect(ctx.destination);
            audioRef.current = { ctx, stream, source, processor };
            setState('listening');
        } catch (err) {
            onErrorRef.current?.(err?.name === 'NotAllowedError'
                ? 'Accès au micro refusé. Autorisez le micro dans le navigateur.'
                : "Impossible d'accéder au micro.");
        }
    }, [state, finish]);

    // manual stop: send what was recorded so far (browser engine: just stop it)
    const stop = useCallback(() => {
        if (recognitionRef.current) { recognitionRef.current.stop(); return; }
        if (captureRef.current) finish();
    }, [finish]);

    useEffect(() => () => {
        captureRef.current = null;
        recognitionRef.current?.abort?.();
        stopAudio();
    }, [stopAudio]);

    return { state, level, start, stop };
}
